import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent
} from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Check,
  ChevronUp,
  Download,
  Expand,
  LoaderCircle,
  Pause,
  PencilLine,
  Play,
  Repeat2,
  RotateCcw,
  Settings2,
  Volume2,
  X
} from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as Dialog from "@radix-ui/react-dialog";
import { api, ApiError, downloadSubtitle } from "../lib/api";
import {
  findActiveCueIndex,
  formatCueTime,
  formatDuration,
  statusLabel
} from "../lib/format";
import { useDocumentTitle } from "../lib/useDocumentTitle";
import { Button } from "../components/Button";
import { PageLoading } from "../components/Loading";
import { EmptyState } from "../components/EmptyState";
import type { Cue, DictionaryEntry, TalkDetail } from "../types";

type SubtitleMode = "bilingual" | "en" | "zh";

function WordText({
  text,
  onWord
}: {
  text: string;
  onWord: (word: string) => void;
}) {
  const pieces = text.split(/([A-Za-z]+(?:['’-][A-Za-z]+)*)/g);
  return (
    <>
      {pieces.map((piece, index) =>
        /^[A-Za-z]/.test(piece) ? (
          <button
            key={`${piece}-${index}`}
            type="button"
            className="inline-word"
            onClick={(event) => {
              event.stopPropagation();
              onWord(piece.replace(/’/g, "'"));
            }}
            tabIndex={-1}
          >
            {piece}
          </button>
        ) : (
          <span key={`${piece}-${index}`}>{piece}</span>
        )
      )}
    </>
  );
}

function ProcessingPanel({ talk }: { talk: TalkDetail }) {
  const queryClient = useQueryClient();
  const retry = useMutation({
    mutationFn: api.retryTalk,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["talk", talk.id] })
  });
  const cancel = useMutation({
    mutationFn: api.cancelTalk,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["talk", talk.id] })
  });
  const isWorking = talk.status === "processing" || talk.status === "queued";

  return (
    <div className="processing-room">
      <div className="processing-visual">
        <div className="processing-orbit" aria-hidden="true">
          <span />
          <span />
          <span />
          <LoaderCircle size={42} />
        </div>
        <span className="eyebrow">
          {isWorking ? "BUILDING YOUR LISTENING COPY" : "PROCESS INTERRUPTED"}
        </span>
        <h1>{isWorking ? statusLabel(talk.status, talk.stage) : "这场演讲还没准备好"}</h1>
        <p>
          {isWorking
            ? "完整视频、英语时间轴和中文译文会依次准备好。你可以离开这个页面，任务会在云端继续。"
            : talk.errorMessage ||
              "任务被中断或处理失败。媒体阶段已完成的部分会保留，重试会从断点继续。"}
        </p>
      </div>
      <div className="processing-meter">
        <div className="processing-meter-head">
          <span>{isWorking ? `${talk.progress}%` : "任务未完成"}</span>
          <span>{statusLabel(talk.status, talk.stage)}</span>
        </div>
        <div className="processing-track">
          <i style={{ width: `${Math.max(3, talk.progress)}%` }} />
        </div>
        <div className="processing-steps">
          {[
            ["downloading", "视频"],
            ["validating", "校验"],
            ["extracting", "音轨"],
            ["transcribing", "英语"],
            ["translating", "中文"]
          ].map(([stage, label], index) => {
            const order = [
              "downloading",
              "validating",
              "extracting",
              "transcribing",
              "translating",
              "ready"
            ];
            const currentIndex = order.indexOf(talk.stage);
            const done = currentIndex > index || talk.status === "ready";
            const active = currentIndex === index;
            return (
              <span
                key={stage}
                className={`${done ? "is-done" : ""} ${active ? "is-active" : ""}`}
              >
                {done ? <Check size={13} /> : <i />}
                {label}
              </span>
            );
          })}
        </div>
      </div>
      <div className="processing-actions">
        {isWorking ? (
          <Button
            variant="ghost"
            onClick={() => cancel.mutate(talk.id)}
            loading={cancel.isPending}
          >
            取消任务
          </Button>
        ) : (
          <Button
            variant="primary"
            icon={<RotateCcw size={16} />}
            onClick={() => retry.mutate(talk.id)}
            loading={retry.isPending}
          >
            从断点重试
          </Button>
        )}
        <Link to="/" className="button button-ghost">
          返回学习库
        </Link>
      </div>
      {retry.isError && (
        <p className="form-error">
          {retry.error instanceof ApiError ? retry.error.message : "重试失败"}
        </p>
      )}
    </div>
  );
}

export function PlayerPage() {
  const { talkId = "" } = useParams();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["talk", talkId],
    queryFn: () => api.talk(talkId),
    enabled: Boolean(talkId),
    refetchInterval: (result) => {
      const status = result.state.data?.status;
      return status === "processing" || status === "queued" ? 2200 : false;
    }
  });
  useDocumentTitle(query.data?.title || "学习页");

  if (query.isLoading) return <PageLoading label="正在准备播放器" />;
  if (query.isError) {
    return (
      <div className="page-frame">
        <EmptyState
          eyebrow="找不到这场演讲"
          title="学习页无法打开"
          body={
            query.error instanceof ApiError
              ? query.error.message
              : "服务连接失败或演讲已被删除。"
          }
          action={<Link to="/" className="button button-secondary">返回学习库</Link>}
        />
      </div>
    );
  }
  const talk = query.data!;
  if (talk.status !== "ready") {
    return (
      <div className="page-frame">
        <Link to="/" className="back-link">
          <ArrowLeft size={15} />
          学习库
        </Link>
        <ProcessingPanel talk={talk} />
      </div>
    );
  }
  return <ReadyPlayer talk={talk} />;
}

function ReadyPlayer({ talk }: { talk: TalkDetail }) {
  const queryClient = useQueryClient();
  const videoRef = useRef<HTMLVideoElement>(null);
  const playerRootRef = useRef<HTMLDivElement>(null);
  const lastSavedRef = useRef(0);
  const resumedRef = useRef(false);
  const activeRowRef = useRef<HTMLDivElement | null>(null);
  const scriptListRef = useRef<HTMLDivElement | null>(null);
  const [currentMs, setCurrentMs] = useState(talk.progressMs);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [mode, setMode] = useState<SubtitleMode>("bilingual");
  const [loopRange, setLoopRange] = useState<{ start: number; end: number } | null>(
    null
  );
  const [lookupWord, setLookupWord] = useState("");
  const [lookupOpen, setLookupOpen] = useState(false);
  const [exportMessage, setExportMessage] = useState("");
  const [showSettings, setShowSettings] = useState(false);
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const videoSource = talk.mediaUrl
    ? `${talk.mediaUrl}${
        window.matchMedia("(max-width: 900px)").matches
          ? "?quality=mobile"
          : ""
      }`
    : undefined;

  const activeIndex = useMemo(
    () => findActiveCueIndex(talk.cues, currentMs),
    [talk.cues, currentMs]
  );
  const activeCue = activeIndex >= 0 ? talk.cues[activeIndex] : null;
  const dictionary = useQuery({
    queryKey: ["dictionary", lookupWord],
    queryFn: () => api.dictionary(lookupWord),
    enabled: lookupOpen && Boolean(lookupWord),
    retry: false
  });

  useEffect(() => {
    if (activeIndex < 0) return;
    const list = scriptListRef.current;
    const row = activeRowRef.current;
    if (!list || !row || !list.contains(row)) return;
    const target = row.offsetTop - list.clientHeight / 2 + row.clientHeight / 2;
    list.scrollTo({
      top: Math.max(0, target),
      behavior: playing ? "smooth" : "auto"
    });
  }, [activeIndex, playing]);

  useEffect(() => {
    return () => {
      const player = videoRef.current;
      if (player && player.currentTime > 0) {
        void api
          .updateProgress(talk.id, Math.round(player.currentTime * 1000))
          .catch(() => undefined);
      }
    };
  }, [talk.id]);

  function seekCue(cue: Cue) {
    const player = videoRef.current;
    if (!player) return;
    player.currentTime = cue.startMs / 1000;
    setCurrentMs(cue.startMs);
    if (!player.paused) void player.play();
  }

  function handleTimeUpdate() {
    const player = videoRef.current;
    if (!player) return;
    const nextMs = Math.round(player.currentTime * 1000);
    setCurrentMs(nextMs);
    if (
      loopRange &&
      player.currentTime >= loopRange.end / 1000 - 0.02
    ) {
      player.currentTime = loopRange.start / 1000;
    }
    if (Date.now() - lastSavedRef.current > 5_000) {
      lastSavedRef.current = Date.now();
      void api.updateProgress(talk.id, nextMs).catch(() => undefined);
    }
  }

  function handleLoadedMetadata() {
    const player = videoRef.current;
    if (!player || resumedRef.current) return;
    resumedRef.current = true;
    if (talk.progressMs > 4_000 && talk.progressMs < talk.durationMs - 8_000) {
      player.currentTime = talk.progressMs / 1000;
    }
  }

  function changeSpeed(nextSpeed: number) {
    setSpeed(nextSpeed);
    if (videoRef.current) videoRef.current.playbackRate = nextSpeed;
  }

  function cycleSpeed() {
    const speeds = [0.75, 1, 1.25, 1.5];
    const next = speeds[(speeds.indexOf(speed) + 1) % speeds.length] ?? 1;
    changeSpeed(next);
  }

  function cycleSubtitleMode() {
    setMode((current) =>
      current === "bilingual" ? "en" : current === "en" ? "zh" : "bilingual"
    );
  }

  function toggleLoop() {
    if (!activeCue) {
      setExportMessage("先播放到一句字幕，再开启本句循环。");
      return;
    }
    if (loopRange) {
      setLoopRange(null);
    } else {
      setLoopRange({ start: activeCue.startMs, end: activeCue.endMs });
    }
  }

  function openWord(word: string) {
    videoRef.current?.pause();
    setTranscriptOpen(false);
    setLookupWord(word);
    setLookupOpen(true);
  }

  async function exportFile(format: "srt" | "vtt", exportMode: SubtitleMode) {
    try {
      await downloadSubtitle(talk, format, exportMode);
      setExportMessage(`已导出 ${format.toUpperCase()} 字幕`);
    } catch {
      setExportMessage("导出失败，请稍后重试。");
    }
  }

  async function enterFullscreen() {
    if (playerRootRef.current?.requestFullscreen) {
      await playerRootRef.current.requestFullscreen();
    } else if (videoRef.current) {
      await videoRef.current.requestFullscreen?.();
    }
  }

  function togglePlay() {
    const player = videoRef.current;
    if (!player) return;
    if (player.paused) void player.play();
    else player.pause();
  }

  function onVideoKeyDown(event: KeyboardEvent<HTMLVideoElement>) {
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      if (videoRef.current)
        videoRef.current.currentTime = Math.max(0, videoRef.current.currentTime - 5);
    }
    if (event.key === "ArrowRight") {
      event.preventDefault();
      if (videoRef.current)
        videoRef.current.currentTime = Math.min(
          videoRef.current.duration || 0,
          videoRef.current.currentTime + 5
        );
    }
    if (event.key === " ") {
      event.preventDefault();
      togglePlay();
    }
  }

  return (
    <div className="player-page v21-player-page">
      <header className="v21-mobile-player-head">
        <Link to="/" className="v21-mobile-player-back" aria-label="返回学习库">
          <ArrowLeft size={18} />
        </Link>
        <div className="v21-mobile-player-title">
          <span>{talk.speaker} · {formatDuration(talk.durationMs)}</span>
          <strong>{talk.title}</strong>
          {talk.titleZh && <small>{talk.titleZh}</small>}
        </div>
        <Link
          to={`/talks/${talk.id}/edit`}
          className="v21-mobile-player-action"
          aria-label="编辑字幕"
        >
          <PencilLine size={17} />
        </Link>
        <details className="v21-mobile-export">
          <summary className="v21-mobile-player-action" aria-label="导出字幕">
            <Download size={17} />
          </summary>
          <div className="v21-mobile-export-menu">
            <button onClick={() => void exportFile("srt", "bilingual")}>双语 SRT</button>
            <button onClick={() => void exportFile("vtt", "bilingual")}>双语 VTT</button>
            <button onClick={() => void exportFile("srt", "en")}>英文 SRT</button>
            <button onClick={() => void exportFile("srt", "zh")}>中文 SRT</button>
          </div>
        </details>
      </header>

      <div className="player-topbar page-frame">
        <Link to="/" className="back-link">
          <ArrowLeft size={15} />
          学习库
        </Link>
        <div className="player-actions">
          <Link to={`/talks/${talk.id}/edit`} className="button button-ghost">
            编辑字幕
          </Link>
          <details className="export-menu">
            <summary className="button button-secondary">
              <Download size={15} />
              导出
            </summary>
            <div className="export-popover">
              <span>双语</span>
              <button onClick={() => exportFile("srt", "bilingual")}>SRT</button>
              <button onClick={() => exportFile("vtt", "bilingual")}>VTT</button>
              <span>单语</span>
              <button onClick={() => exportFile("srt", "en")}>英文 SRT</button>
              <button onClick={() => exportFile("srt", "zh")}>中文 SRT</button>
            </div>
          </details>
          <button
            className="icon-button"
            onClick={() => setShowSettings((value) => !value)}
            aria-label="播放设置"
          >
            <Settings2 size={17} />
          </button>
        </div>
      </div>

      <header className="talk-heading page-frame">
        <div>
          <span className="eyebrow">{talk.speaker} · TED TALK</span>
          <h1>{talk.title}</h1>
          {talk.titleZh && <p className="talk-heading-zh">{talk.titleZh}</p>}
        </div>
        <div className="talk-heading-meta">
          <span>{talk.cues.length} 句字幕</span>
          <i />
          <span>{formatDuration(talk.durationMs)}</span>
          <i />
          <span>本地视频</span>
        </div>
      </header>

      {showSettings && (
        <div className="player-settings page-frame">
          <div>
            <span>字幕显示</span>
            <div className="segmented">
              {(
                [
                  ["bilingual", "中英双语"],
                  ["en", "仅英文"],
                  ["zh", "仅中文"]
                ] as const
              ).map(([value, label]) => (
                <button
                  key={value}
                  className={mode === value ? "is-active" : ""}
                  onClick={() => setMode(value)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <span>播放速度</span>
            <div className="segmented">
              {[0.5, 0.75, 1, 1.25, 1.5].map((value) => (
                <button
                  key={value}
                  className={speed === value ? "is-active" : ""}
                  onClick={() => changeSpeed(value)}
                >
                  {value}×
                </button>
              ))}
            </div>
          </div>
          <button
            className={`loop-control ${loopRange ? "is-active" : ""}`}
            onClick={toggleLoop}
          >
            <Repeat2 size={16} />
            {loopRange ? "本句循环中" : "循环当前句"}
          </button>
        </div>
      )}

      <div className="study-layout page-frame">
        <section className="screening-room">
          <div
            ref={playerRootRef}
            className="player-frame"
            data-mode={mode}
          >
            <video
              ref={videoRef}
              src={videoSource}
              poster={talk.posterUrl || talk.thumbnailUrl || undefined}
              controls
              playsInline
              preload="metadata"
              onLoadedMetadata={handleLoadedMetadata}
              onTimeUpdate={handleTimeUpdate}
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              onEnded={() => {
                setPlaying(false);
                void api.updateProgress(talk.id, 0).catch(() => undefined);
              }}
              onKeyDown={onVideoKeyDown}
              tabIndex={0}
            />
            {activeCue && (
              <div className="subtitle-overlay" aria-live="off">
                {mode !== "zh" && (
                  <strong>
                    <WordText text={activeCue.en} onWord={openWord} />
                  </strong>
                )}
                {mode !== "en" && <span>{activeCue.zh}</span>}
              </div>
            )}
            <div className="player-watermark" aria-hidden="true">
              <span>LISTEN</span>
              <i />
              <span>UNDERSTAND</span>
            </div>
          </div>

          <div className="v21-mobile-player-controls" aria-label="播放控制">
            <button
              type="button"
              className="v21-mobile-play"
              onClick={togglePlay}
              aria-label={playing ? "暂停" : "播放"}
            >
              {playing ? (
                <Pause size={17} fill="currentColor" />
              ) : (
                <Play size={17} fill="currentColor" />
              )}
            </button>
            <button
              type="button"
              className={`v21-mobile-control ${loopRange ? "is-active" : ""}`}
              onClick={toggleLoop}
            >
              <Repeat2 size={15} />
              {loopRange ? "循环中" : "本句"}
            </button>
            <button type="button" className="v21-mobile-control" onClick={cycleSpeed}>
              {speed}×
            </button>
            <span className="v21-control-spacer" />
            <button
              type="button"
              className="v21-mobile-control"
              onClick={cycleSubtitleMode}
            >
              {mode === "bilingual" ? "中英" : mode === "en" ? "EN" : "中"}
            </button>
            <button
              type="button"
              className="v21-mobile-icon-control"
              onClick={() => void enterFullscreen()}
              aria-label="全屏"
            >
              <Expand size={16} />
            </button>
          </div>

          <div className="player-utility">
            <div className="transport-controls">
              <button
                className="transport-play"
                onClick={togglePlay}
                aria-label={playing ? "暂停" : "播放"}
              >
                {playing ? <Pause size={18} fill="currentColor" /> : <Play size={18} fill="currentColor" />}
              </button>
              <button
                className={`transport-chip ${loopRange ? "is-active" : ""}`}
                onClick={toggleLoop}
              >
                <Repeat2 size={15} />
                {loopRange ? "本句循环中" : "本句循环"}
              </button>
              <div className="speed-inline" aria-label="播放速度">
                {[0.75, 1, 1.25].map((value) => (
                  <button
                    key={value}
                    className={speed === value ? "is-active" : ""}
                    onClick={() => changeSpeed(value)}
                  >
                    {value}×
                  </button>
                ))}
              </div>
            </div>
            <div className="transport-aside">
              <span>
                <Volume2 size={15} />
                云端安全播放
              </span>
              <button
                className="icon-button"
                onClick={() => void enterFullscreen()}
                aria-label="全屏"
              >
                <Expand size={17} />
              </button>
            </div>
          </div>

          <div className="talk-note">
            <span className="talk-note-label">观看提示</span>
            <p>
              点击字幕中的任意单词会暂停并查词；点击句子左侧时间码可跳转。字幕时间轴可直接在编辑页校正。
            </p>
          </div>
        </section>

        <aside className="script-panel v21-script-panel" aria-label="双语字幕">
          <header className="v21-transcript-head">
            <div className="v21-transcript-label">
              <span>Transcript</span>
              <small>
                {String(activeIndex >= 0 ? activeIndex + 1 : 0).padStart(2, "0")} /{" "}
                {String(talk.cues.length).padStart(2, "0")}
              </small>
            </div>
            <button
              type="button"
              className="v21-transcript-expand"
              onClick={() => setTranscriptOpen(true)}
              aria-label="展开全部字幕"
            >
              <ChevronUp size={15} />
            </button>
          </header>
          <div className="script-list" ref={scriptListRef}>
            {talk.cues.map((cue, index) => (
              <div
                key={cue.id}
                ref={index === activeIndex ? activeRowRef : undefined}
                className={`script-cue ${index === activeIndex ? "is-active" : ""}`}
                role="button"
                tabIndex={0}
                onClick={() => seekCue(cue)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    seekCue(cue);
                  }
                }}
              >
                <span className="cue-time">{formatCueTime(cue.startMs)}</span>
                <div className="cue-copy">
                  <p className="cue-en">
                    <WordText text={cue.en} onWord={openWord} />
                  </p>
                  <p className="cue-zh">{cue.zh}</p>
                </div>
                {index === activeIndex && (
                  <span className="cue-live" aria-label="当前句">
                    <i />
                  </span>
                )}
              </div>
            ))}
          </div>
        </aside>
      </div>

      {exportMessage && (
        <button className="toast" onClick={() => setExportMessage("")}>
          {exportMessage}
          <X size={14} />
        </button>
      )}

      <Dialog.Root open={lookupOpen} onOpenChange={setLookupOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="lookup-overlay" />
          <Dialog.Content className="lookup-sheet">
            <Dialog.Close className="dialog-close" aria-label="关闭释义">
              <X size={18} />
            </Dialog.Close>
            <span className="eyebrow">WORD IN CONTEXT</span>
            <Dialog.Title className="lookup-word">{lookupWord}</Dialog.Title>
            {dictionary.isLoading && (
              <div className="lookup-loading">
                <LoaderCircle size={18} className="animate-spin" />
                正在查词
              </div>
            )}
            {dictionary.isError && (
              <Dialog.Description className="form-error">
                {dictionary.error instanceof ApiError
                  ? dictionary.error.message
                  : "查词失败"}
              </Dialog.Description>
            )}
            {dictionary.data && <DictionaryContent entry={dictionary.data} />}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <Dialog.Root open={transcriptOpen} onOpenChange={setTranscriptOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="v21-transcript-overlay" />
          <Dialog.Content className="v21-transcript-sheet">
            <Dialog.Close className="dialog-close" aria-label="关闭完整字幕">
              <X size={18} />
            </Dialog.Close>
            <header className="v21-transcript-sheet-head">
              <Dialog.Title>Transcript</Dialog.Title>
              <span>{talk.cues.length} 句</span>
            </header>
            <Dialog.Description className="sr-only">
              完整中英双语字幕列表，点击句子可跳转到对应时间。
            </Dialog.Description>
            <div className="v21-transcript-sheet-list">
              {talk.cues.map((cue, index) => (
                <div
                  key={cue.id}
                  className={`v21-sheet-cue ${index === activeIndex ? "is-active" : ""}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    seekCue(cue);
                    setTranscriptOpen(false);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      seekCue(cue);
                      setTranscriptOpen(false);
                    }
                  }}
                >
                  <span>{formatCueTime(cue.startMs)}</span>
                  <div>
                    <p className="cue-en">
                      <WordText text={cue.en} onWord={openWord} />
                    </p>
                    <p className="cue-zh">{cue.zh}</p>
                  </div>
                </div>
              ))}
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}

function DictionaryContent({ entry }: { entry: DictionaryEntry }) {
  return (
    <div className="dictionary-content">
      {entry.phonetic && <span className="dictionary-phonetic">{entry.phonetic}</span>}
      <p className="dictionary-translation">{entry.translation}</p>
      {entry.definition && <p className="dictionary-definition">{entry.definition}</p>}
      <span className="dictionary-source">
        {entry.source === "ecdict" ? "本地 ECDICT" : "DeepSeek 辅助释义"}
      </span>
    </div>
  );
}

